"use client"

import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import type { EventDetail } from "@/types/glassroom"
import {
  Badge,
  Button,
  DetailRow,
  Paper,
  TbActivity,
  TbCalendar,
  TbExternalLink,
  TbMapPin,
  TbNote,
  TbQrcode,
  TbTarget,
  TbTrees,
  Text,
} from "@forestsync/ui"
import { QRCode } from "@/components/glassroom/common"
import { HyperLink } from "@/components/glassroom/common/HyperLink"

function formatDate(value: Date | string | null | undefined) {
  if (!value) return "-"

  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return "-"

  return date.toLocaleDateString()
}

export function EventDetailView({ event }: { event: EventDetail }) {
  const t = useTranslations()
  const [currentUrl, setCurrentUrl] = useState("")

  useEffect(() => {
    setCurrentUrl(window.location.href)
  }, [])

  const sinkLocation = useMemo(() => {
    return [event.sinkCountry, event.sinkRegion, event.sinkMunicipality]
      .filter(Boolean)
      .join(", ")
  }, [event.sinkCountry, event.sinkMunicipality, event.sinkRegion])

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="flex flex-col gap-4 md:col-span-5">
          <Paper withBorder radius="lg" p="md" className="flex flex-col shadow-md h-full min-h-50">
            <div className="mb-4">
              <Text size="lg" fw={700} className="mb-2">
                {t("event.title")}
              </Text>
              <Text size="xl" className="font-mono text-gray-700 dark:text-gray-300 break-all">
                {event.refId}
              </Text>
            </div>
            <div className="flex-1 flex items-center justify-center">
              {currentUrl ? (
                <QRCode key={currentUrl} value={currentUrl} size={200} className="mx-auto" />
              ) : (
                <TbQrcode size={64} className="text-blue-400 dark:text-blue-300" />
              )}
            </div>
          </Paper>
        </div>

        <div className="flex flex-col gap-4 md:col-span-7">
          <Paper withBorder radius="lg" p="md" className="shadow-md">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-4 mb-4">
              <div>
                <Text size="sm" c="dimmed" className="mb-1">
                  {t("event.tonsAffected")}
                </Text>
                <Text size="2.2rem" fw={800} className="leading-none">
                  {event.tokenCount}
                </Text>
              </div>
              <Badge variant="light" color="blue" size="lg">
                {event.eventTypeName}
              </Badge>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              <DetailRow
                icon={TbTarget}
                label={t("event.tonsAffected")}
                value={event.tokenCount.toString()}
                className="py-2"
              />
              <DetailRow
                icon={TbActivity}
                label={t("sink.eventType")}
                value={event.eventTypeName}
                className="py-2"
              />
              <DetailRow
                icon={TbCalendar}
                label={t("sink.timestamp")}
                value={formatDate(event.timestamp)}
                className="py-2"
              />
              <DetailRow
                icon={TbNote}
                label={t("common.description")}
                value={event.description || t("event.noDescription")}
                className="py-2"
                layout="paragraph"
              />
            </div>
          </Paper>

          <Paper withBorder radius="lg" p="md" className="shadow-md">
            <Text fw={700} size="md" className="mb-2">
              {t("event.metadata")}
            </Text>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              <DetailRow
                icon={TbActivity}
                label={t("common.refId")}
                value={<span className="font-mono">{event.refId}</span>}
                className="py-2"
              />
              <DetailRow
                icon={TbTrees}
                label={t("event.sink")}
                value={
                  <HyperLink type="sink" refId={event.sinkRefId}>
                    {event.sinkName || event.sinkRefId}
                  </HyperLink>
                }
                className="py-2"
              />
              <DetailRow
                icon={TbMapPin}
                label={t("common.location")}
                value={sinkLocation || "-"}
                className="py-2"
              />
              <DetailRow
                icon={TbExternalLink}
                label={t("sink.transaction")}
                value={
                  event.transactionRefId ? (
                    <Button
                      component={Link}
                      href={`/transaction/${event.transactionRefId}`}
                      variant="light"
                      size="xs"
                      leftSection={<TbExternalLink size={12} />}
                    >
                      {t("sink.viewTransaction")}
                    </Button>
                  ) : (
                    "-"
                  )
                }
                className="py-2"
              />
            </div>
          </Paper>
        </div>
      </div>
    </div>
  )
}
